import React from 'react';
import { useParams, Link } from 'react-router-dom';

const projectData = {
  mapnitor: {
    title: 'Mapnitor',
    category: 'Product Design & Analytics',
    client: 'Mapnitor Inc.',
    year: '2025',
    overview: 'Mapnitor is a cutting-edge analytics dashboard designed to visualize massive datasets in real-time. The goal was to take complex geographical and statistical data and render it into a beautifully simple, actionable UI.',
    img: '/assets/mapnitor.png',
    screens: [
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1600&auto=format&fit=crop'
    ],
    features: ['Real-time Data Sync', 'Interactive WebGL Maps', 'Custom UI Component Library']
  },
  blogator: {
    title: 'Blogator',
    category: 'AI Web Application',
    client: 'Content Studio',
    year: '2026',
    overview: 'Blogator harnesses the power of custom AI models to automatically generate, optimize, and publish SEO-driven content. We built the complete web platform from landing page to the robust SaaS dashboard.',
    img: '/assets/blogator.png',
    screens: [
      'https://images.unsplash.com/photo-1611162617474-5b21e879e113?q=80&w=1600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?q=80&w=1600&auto=format&fit=crop'
    ],
    features: ['LLM Integration', 'One-Click Publishing', 'Subscription Management']
  },
  tinyfocus: {
    title: 'TinyFocus',
    category: 'Web Design & Productivity',
    client: 'Independent',
    year: '2024',
    overview: 'A minimalist productivity timer that lives in your browser. TinyFocus was built to help users reclaim their attention span without bloated features. We delivered a seamless, ultra-fast web experience.',
    img: '/assets/tinyfocus.png',
    screens: [
      'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=1600&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=80&w=1600&auto=format&fit=crop'
    ],
    features: ['Zero Distraction UI', 'Local Storage Sync', 'Pomodoro Tracking']
  }
};

export default function ProjectDetail() {
  const { id } = useParams();
  const project = projectData[id];

  if (!project) {
    return (
      <div className="min-h-screen pt-40 px-6 max-w-7xl mx-auto">
        <h1 className="text-4xl">Project Not Found</h1>
        <Link to="/projects" className="text-blue-600 mt-4 block">Return to Works</Link>
      </div>
    );
  }

  return (
    <article className="pt-32 pb-32">
      <div className="px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <Link to="/projects" className="text-zinc-500 hover:text-zinc-900 transition flex items-center gap-2 mb-10 text-sm font-mono tracking-widest uppercase">
            <span>←</span> Back to Works
          </Link>
          <h1 className="text-5xl md:text-7xl font-medium tracking-tight text-zinc-900 mb-6">{project.title}</h1>
        </div>

        {/* Project Meta */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 border-t border-zinc-200 pt-12 mb-20">
          <div className="md:col-span-2">
            <h3 className="text-sm font-mono text-zinc-400 uppercase tracking-widest mb-4">Overview</h3>
            <p className="text-xl text-zinc-600 font-light leading-relaxed">{project.overview}</p>
          </div>
          <div>
            <h3 className="text-sm font-mono text-zinc-400 uppercase tracking-widest mb-4">Client</h3>
            <p className="text-lg font-medium text-zinc-900">{project.client}</p>
            <h3 className="text-sm font-mono text-zinc-400 uppercase tracking-widest mb-4 mt-8">Year</h3>
            <p className="text-lg font-medium text-zinc-900">{project.year}</p>
          </div>
          <div>
            <h3 className="text-sm font-mono text-zinc-400 uppercase tracking-widest mb-4">Services</h3>
            <ul className="space-y-2">
              <li className="text-lg font-medium text-zinc-900">{project.category}</li>
              {project.features.map((feat, i) => (
                <li key={i} className="text-zinc-500 font-light">{feat}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Main Imagery */}
      <div className="w-full bg-zinc-100 aspect-video mb-20 flex items-center justify-center overflow-hidden">
        <img src={project.img} alt={project.title} onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?q=80&w=2000&auto=format&fit=crop' }} className="w-full h-full object-cover" />
      </div>

      <div className="px-6 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
        {project.screens.map((screen, idx) => (
          <div key={idx} className="bg-zinc-100 rounded-2xl overflow-hidden aspect-square">
            <img src={screen} alt="Screen Preview" className="w-full h-full object-cover hover:scale-105 transition duration-700" />
          </div>
        ))}
      </div>
      
      <div className="text-center mt-32">
        <h2 className="text-4xl font-medium tracking-tight mb-8">Ready for your own?</h2>
        <Link to="/contact" className="inline-block px-10 py-5 bg-zinc-900 text-white rounded-full font-medium hover:bg-zinc-800 transition shadow-xl shadow-zinc-900/10">
          Start a Project
        </Link>
      </div>
    </article>
  );
}
